import { defineStore } from "pinia";
import { useUserStore } from "~/store/user";
import { useWidgetStore } from "~/store/widget";
import { useCompanyStore } from "~/store/company";
import { useCookie, useNuxtApp } from "#app";
import type {
  ActivityLog,
  Document,
  DuplicateActionEnum,
  FlowStateResponse,
  HistoryEvent,
  Item,
  ItemPaginationResponseObject,
  ItemReminder,
  Note,
  RemindItemRequest,
  User,
  ValidationResult
} from "~~/api/models";

import { piniaPluginPersistedstate } from "#imports";
import type { CachedDocuments, CachedFilters, FilterPill } from "~/components/documents/utils";
import { ValidationError } from "~/services/ApiService";
import { FetchError } from "ofetch";
import { useEventStore } from '~/store/useEvents';
import { useCompanyFilterStore } from '~/store/companyFilter';

export const useItemStore = defineStore('item', () => {
  // State
  const items = ref<ItemPaginationResponseObject>({} as ItemPaginationResponseObject);
  const loadingItems = ref(true);
  const filters = ref<FilterPill[]>([]);
  const cachedFilters = ref<CachedFilters>({});
  const selectedItems = ref<string[]>([]);
  const workingOnSelected = ref(false);
  const reminders = ref<ItemReminder[]>([]);
  const workingItemFlowState = ref<FlowStateResponse | null>(null);
  const workingItemDuplicates = ref<Item[] | null>(null);
  const workingItem = ref<Item | null>(null);
  const editableItem = ref<Item | null>(null);
  const currentAssignees = ref<User[]>([]);
  const lastItemReminder = ref<ItemReminder | null>(null);
  const activeTenant = ref<string | null>(null);
  const activeDocuments = ref<Document[]>([]);
  const cachedDocuments = ref<CachedDocuments>({});
  const showDocumentComments = ref<boolean>(false);

  // Composable
  const nuxtApp = useNuxtApp();
  const widgetStore = useWidgetStore();
  const userStore = useUserStore();
  const companyStore = useCompanyStore();
  const storeEvent = useEventStore();
  const companyFilterStore = useCompanyFilterStore();

  // Cleanup
  const cleanItemStore = () => {
    resetStore();

    useCookie('item.active').value = null;
  };

  // Helpers
  const handleValidationError = (error: unknown, item: Item) => {
    if (error instanceof ValidationError && error.details) {
      item.validationResults = error.details as ValidationResult[];
      throw error.details;
    }
    throw error;
  };

  // Actions
  const getItemById = async (itemId: string, fields: string | null = null, widgetName: string | null = null): Promise<Item | null> => {
    const item = items.value?.data?.find((item) => item.id === itemId);

    if (item) {
      // If the fields are not null, check if the item has all the fields requested
      if (fields) {
        const missingFields = fields.split(',').filter(field => !Object.hasOwn(item, field));
        if (missingFields.length === 0) {
          // If the item does not have all the requested fields, return null
          return item;
        }
      } else // If the item exist, but the fields are null, return the item
        return item;
    }

    let widgetId: string | null = null;
    if (widgetName)
      widgetId = widgetStore.widgets.find((w) => w.normalizedName == widgetName)?.normalizedName ?? null;

    nuxtApp.$apiService.tenants = userStore.serialNumbers as string[];
    return await nuxtApp.$apiService.Item.findItem(itemId, fields, widgetId);
  };

  const getItems = async (
    page: number = 1,
    pageSize: number = storeEvent.defaultPagination,
    fields: string | null = null,
    filters: FilterPill[] | null = null,
    sort: string | null = null,
  ): Promise<ItemPaginationResponseObject> => {
    const serialNumbers = companyFilterStore.effectiveSerialNumbers;

    if (widgetStore.activeWidget && serialNumbers.length > 0) {
      nuxtApp.$apiService.tenants = serialNumbers;
      nuxtApp.$apiService.activeWidget = widgetStore.activeWidget;

      if (sort && !sort.toLowerCase().includes('created'))
        sort += ',created:desc';

      return await nuxtApp.$apiService.Item.getItems(page, pageSize, fields, filters ?? [], sort);
    }

    return {} as ItemPaginationResponseObject;
  };

  const getAllItems = async (page: number = 1, pageSize: number = storeEvent.defaultPagination, fields: string | null = null) => {
    const serialNumbers = companyFilterStore.effectiveSerialNumbers;

    if (serialNumbers.length > 0 && widgetStore.activeWidget) {
      loadingItems.value = true;
      nuxtApp.$apiService.tenants = serialNumbers as string[];
      nuxtApp.$apiService.activeWidget = widgetStore.activeWidget;

      if (!serialNumbers || !widgetStore.activeWidget || serialNumbers.length === 0) {
        clearItems();
        return items.value;
      }

      try {
        items.value = await nuxtApp.$apiService.Item.getAllItems(page, pageSize, fields);
        loadingItems.value = false;
      } catch (e) {
        console.error(e);
      }
    } else {
      clearItems();
    }

    return items.value;
  };

  const getFilteredItems = async (
    page: number = 1,
    pageSize: number = storeEvent.defaultPagination,
    filters: { value: string; type: string; label: string }[],
    fields: string | null = null
  ) => {
    const serialNumbers = companyFilterStore.effectiveSerialNumbers;

    if (serialNumbers.length > 0 && widgetStore.activeWidget) {
      loadingItems.value = true;
      nuxtApp.$apiService.tenants = serialNumbers as string[];
      nuxtApp.$apiService.activeWidget = widgetStore.activeWidget;

      try {
        items.value = await nuxtApp.$apiService.Item.getFilteredItems(page, pageSize, filters, fields);
        loadingItems.value = false;
      } catch (e) {
        console.error(e);
      }
    } else {
      clearItems();
    }

    return items.value;
  };

  const clearItems = () => {
    items.value = {
      data: [],
      hasNextPage: false,
      hasPreviousPage: false,
      page: 1,
      pageSize: storeEvent.defaultPagination,
      totalCount: 0,
      totalPages: 0
    };
  };

  const setLoadingItems = (value: boolean) => {
    loadingItems.value = value;
  };

  const sendReminder = async (RemindItemRequest: RemindItemRequest[] | RemindItemRequest) => {
    const itemReminders: ItemReminder[] = (await nuxtApp.$apiService.Item.sendReminder(RemindItemRequest)) as ItemReminder[];

    itemReminders.forEach((itemReminder) => {
      const index = reminders.value.findIndex((reminder) => reminder.itemId === itemReminder.itemId);

      if (index > -1) {
        reminders.value[index] = itemReminder;
      } else {
        reminders.value.push(itemReminder);
      }
    });

    return itemReminders;
  };

  const sendReminderToAllAssignees = async (returnUrl: string, filters: FilterPill[] | null = null) => {
    const itemReminders: ItemReminder[] = (await nuxtApp.$apiService.Item.sendReminderToAllAssignees(returnUrl, filters)) as ItemReminder[];

    itemReminders.forEach((itemReminder) => {
      const index = reminders.value.findIndex((reminder) => reminder.itemId === itemReminder.itemId);

      if (index > -1) {
        reminders.value[index] = itemReminder;
      } else {
        reminders.value.push(itemReminder);
      }
    });

    return itemReminders;
  };

  const getReminders = async (itemIds: string[], force = false) => {
    if (!force) {
      const existingItemIds = reminders.value.map((reminder) => reminder.itemId);
      itemIds = itemIds.filter((itemId) => !existingItemIds.includes(itemId));
    }

    if (itemIds.length === 0) {
      return reminders.value;
    }
    const newReminders = await nuxtApp.$apiService.Item.getReminders(itemIds);

    newReminders.forEach((itemReminder) => {
      const index = reminders.value.findIndex((reminder) => reminder.itemId === itemReminder.itemId);

      if (index > -1) {
        reminders.value[index] = itemReminder;
      } else {
        reminders.value.push(itemReminder);
      }
    });

    return reminders.value;
  };

  const approveItem = async (force: boolean = false): Promise<void> => {
    if (!workingItem.value) return;
    try {
      await nuxtApp.$apiService.Item.approveItem(workingItem.value.id as string, force);
    } catch (error: unknown) {
      handleValidationError(error, workingItem.value);
    }
  };
  const declineItem = async (reason: string): Promise<void> => {
    if (!workingItem.value) return;
    await nuxtApp.$apiService.Item.declineItem(workingItem.value.id as string, workingItem.value.tenantId as string, reason);
  };
  const startFlow = async (force: boolean = false): Promise<void> => {
    if (!workingItem.value) return;
    try {
      await nuxtApp.$apiService.Item.startFlow(workingItem.value.id as string, force);
    } catch (error: unknown) {
      handleValidationError(error, workingItem.value);
    }
  };

  const getWorkingItemFlowState = async (): Promise<FlowStateResponse | null> => {
    try {
      if (!workingItem.value) return null;

      workingItemFlowState.value = await nuxtApp.$apiService.Item.getFlowState(workingItem.value.id as string, workingItem.value.tenantId as string);
    } catch (error: unknown) {
      console.error(error);
      return null;
    }

    return workingItemFlowState.value;
  };

  const getFlowStatus = async (itemId: string, tenantId: string): Promise<FlowStateResponse | null> => {
    try {
      return await nuxtApp.$apiService.Item.getFlowState(itemId, tenantId);
    } catch (error: unknown) {
      console.error(error);
      return null;
    }
  };

  const getItemActivities = async (itemId: string): Promise<ActivityLog[]> => {
    if (!workingItem.value) return [];
    nuxtApp.$apiService.activeWidget = widgetStore.activeWidget;
    return await nuxtApp.$apiService.Item.getItemActivities(itemId, workingItem.value.tenantId as string);
  };

  const getItemHistoryEvents = async (itemId: string): Promise<HistoryEvent[]> => {
    if (!workingItem.value) return [];
    nuxtApp.$apiService.activeWidget = widgetStore.activeWidget;
    return await nuxtApp.$apiService.Item.getItemHistoryEvents(itemId, workingItem.value.tenantId as string);
  };

  const getDuplicates = async (): Promise<Item[] | null> => {
    try {
      if (!workingItem.value) return null;
      workingItemDuplicates.value = await nuxtApp.$apiService.Item.getDuplicates(workingItem.value.id as string);
    } catch (e: unknown) {
      console.error(e);
    }

    return workingItemDuplicates.value;
  };

  const updateDuplicateStatus = async (duplicateActionEnum: DuplicateActionEnum): Promise<boolean | null> => {
    if (!workingItem.value) return null;
    return await nuxtApp.$apiService.Item.updateDuplicateStatus(workingItem.value.id as string, duplicateActionEnum);
  };

  const getCachedFilters = (normalizedWidgetName: string): FilterPill[] => {
    if (!cachedFilters.value[normalizedWidgetName]) return [];

    return cachedFilters.value[normalizedWidgetName];
  };

  const setFilters = (newFilters: FilterPill[]) => {
    const activeWidget = widgetStore.activeWidget;

    filters.value = newFilters;

    if (activeWidget && activeWidget.normalizedName) {
      cachedFilters.value[activeWidget.normalizedName] = newFilters;
    }
  };

  const clearFilters = () => {
    filters.value = [];
    cachedFilters.value = {};
  };

  const getItemDocuments = async (itemId: string | null | undefined, tenantId: string | null = null): Promise<Document[]> => {
    const resolvedItemId = itemId ? String(itemId) : null;
    const resolvedTenantId = tenantId ?? activeTenant.value;

    if (!resolvedItemId || !resolvedTenantId) return [];
    const cachedDocuments = getCachedDocuments(resolvedItemId, resolvedTenantId);

    if (cachedDocuments.length > 0) {
      setActiveDocuments(cachedDocuments);
      return activeDocuments.value;
    }

    const itemDocuments: Document[] = await nuxtApp.$apiService.Item.getItemDocuments(resolvedItemId, tenantId) as Document[];
    setActiveDocuments(itemDocuments);
    setCachedDocuments(resolvedItemId, resolvedTenantId, itemDocuments);

    return activeDocuments.value;
  };

  const getWorkingItem = async (itemId: string): Promise<Item | null> => {
    let item = null;

    if (!widgetStore.activeWidget) return null;

    nuxtApp.$apiService.activeWidget = widgetStore.activeWidget;
    if (activeTenant.value) {
      try {
        nuxtApp.$apiService.activeTenant = activeTenant.value;
        item = await nuxtApp.$apiService.Item.getItem(itemId);
      } catch (error: unknown) {
        // If the item was not found, we might be trying to navigate to an item for a different tenant.
        // In that case, we should clear the active tenant and try again.
        if (error instanceof FetchError && error.status === 404) {
          clearActiveTenant();
          item = await getWorkingItem(itemId);
        }
      }
    } else {
      nuxtApp.$apiService.tenants = userStore.allSerialNumbers as string[];
      item = await nuxtApp.$apiService.Item.findItem(itemId);

      if (item && item?.tenantId)
        await userStore.validateActiveOrganisation(String(item.tenantId));
    }

    if (item) {
      setWorkingItem(item);
      return workingItem.value;
    } else {
      clearWorkingItem();
      return null;
    }
  };

  const setWorkingItem = (item: Item, updatedItemFields: Item | null = null) => {
    workingItem.value = item;
    if (updatedItemFields)
      editableItem.value = {...editableItem.value, ...updatedItemFields};
    else
      editableItem.value = {...item};

    if (item.tenantId) {
      setActiveTenant(item.tenantId);
    }
  };

  const isAccessible = async (itemId: string): Promise<boolean> => {
    return await nuxtApp.$apiService.Item.isItemAccessible(itemId);
  };

  /**
   * Updates an item in the current tenant or in a specific tenant.
   * @param itemId
   * @param item
   *  Usually we are saving the item for the active tenant. But in some cases, we might have reset the active tenant before attempting to save an item.
   *  This would happen when leaving routes, or switching between tenants and the item was not saved.
   * @param tenantId
   */
  const updateItem = async (itemId: string, item: Partial<Item>, tenantId: string | null = null) => {
    if (!tenantId)
      nuxtApp.$apiService.activeTenant = activeTenant.value as string;

    let fieldsFilter = Object.keys(item).join(',');

    // If any validation results are present, we should include them in the fieldsFilter.
    if (workingItem.value?.validationResults && workingItem.value?.validationResults.length > 0) {
      fieldsFilter += ',validationResults';
    }

    const updatedItem = await nuxtApp.$apiService.Item.updateItem(itemId, item, fieldsFilter);
    if (!updatedItem) return null;

    // The updatedItem will be an item object containing only the fields that were updated. We need to merge the updated fields with the original item to get the full item object.
    const updatedItemWithFullFields = {...workingItem.value, ...updatedItem};
    setWorkingItem(updatedItemWithFullFields, updatedItem);

    return editableItem.value;
  };

  const getValidationResults = async () => {
    if (workingItem.value) {
      const item = await nuxtApp.$apiService.Item.getItem(workingItem.value.id as string, 'validationResults');
      if (item && item.validationResults) {
        // Update the validation results in the item store
        workingItem.value.validationResults = item.validationResults;
      }
    }
  };

  const getCurrentAssignees = async (): Promise<User[] | null> => {
    if (workingItem.value) {
      if (workingItem.value.owners && workingItem.value.owners.length > 0) {
        const owners: string[] = workingItem.value.owners.map((owner) => String(owner.userId));
        // If there are no owners, return an empty array
        if (owners.length === 0) return [];

        // Make sure to fetch the users that we are referencing
        const users = await companyStore.getUsers(owners);

        // Fetch the users object for each owner
        currentAssignees.value = users.filter((user) => owners.includes(user.id));
        return currentAssignees.value;
      } else currentAssignees.value = [];
    }

    currentAssignees.value = [];
    return null;
  };

  const getLastItemReminder = async (): Promise<ItemReminder | null> => {
    if (workingItem.value && currentAssignees.value.length > 0) {
      const firstAssignee = currentAssignees.value[0];
      if (!firstAssignee) {
        lastItemReminder.value = null;
        return null;
      }

      const reminders = await nuxtApp.$apiService.Item.getItemRemindersForUser(
        workingItem.value.id as string,
        firstAssignee.id as string
      );

      if (reminders && reminders.length > 0) {
        const sortedReminders = reminders.sort((a, b) => new Date(a.remindedAt as string).getTime() - new Date(b.remindedAt as string).getTime()).reverse();
        lastItemReminder.value = sortedReminders[0] || null;
        return lastItemReminder.value;
      } else lastItemReminder.value = null;
    }

    lastItemReminder.value = null;
    return null;
  };

  const addComment = async (message: string): Promise<Note | null> => {
    if (!workingItem.value) return null;
    return await nuxtApp.$apiService.Item.addComment(workingItem.value.id as string, message, workingItem.value.tenantId as string);
  };

  const deleteAttachment = async (documentId: string): Promise<boolean> => {
    if (!workingItem.value) return false;
    try {
      return await nuxtApp.$apiService.Item.deleteAttachment(workingItem.value.id as string, documentId);
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      return false;
    }

  };
  const createDocumentCacheKey = (itemId: string, tenantId: string) => {
    return `${tenantId}:${itemId}`;
  };

  const setCachedDocuments = (itemId: string, tenantId: string, documents: Document[]) => {
    cachedDocuments.value[createDocumentCacheKey(itemId, tenantId)] = documents;
  };

  const getCachedDocuments = (itemId: string, tenantId: string) => {
    const cacheKey = createDocumentCacheKey(itemId, tenantId);

    if (!cachedDocuments.value[cacheKey]) return [];

    return cachedDocuments.value[cacheKey];
  };

  const setActiveDocuments = (documents: Document[]) => {
    activeDocuments.value = documents;
  };

  const clearWorkingItem = () => {
    workingItem.value = null;
    activeTenant.value = null;
    activeDocuments.value = [];
  };

  const setActiveTenant = (tenantId: string) => {
    activeTenant.value = tenantId;
    nuxtApp.$apiService.activeTenant = tenantId;
  };

  const clearActiveTenant = () => {
    activeTenant.value = null;
  };

  const setWorkingOnSelected = (value: boolean) => {
    workingOnSelected.value = value;
  };

  const setShowDocumentComments = (value: boolean) => {
    showDocumentComments.value = value;
  };

  const fieldValidationIssues = (prop: string, optional: boolean = false): ValidationResult[] => {
    if (!workingItem.value?.validationResults || workingItem.value?.validationResults.length === 0) return [];

    return workingItem.value.validationResults.filter(validation =>
      (validation.errorType === 'FIELD' || validation.errorType === 'BOTH') &&
      validation.fieldPath.toLowerCase() === prop.toLowerCase()
      && (validation.optional === optional)
    );
  };

  const getValidationTranslation = (
    validation: ValidationResult,
    t: (key: string, params?: (Record<string, string> | null)) => string
  ) => {
    let validationParams = {};

    // Validation will include a json object with translation parameters, e.g., {[fieldKey]: 'fieldTranslation'}.
    // This method needs to return the {key: translatedValue} object for the translation.
    if (validation?.errorParametersDictionary) {
      const keys = Object.keys(validation.errorParametersDictionary);
      const values = Object.values(validation.errorParametersDictionary);

      // run through the keys and map them to the values. The value should be translated so we would return something like {fieldKey: 'fieldTranslation'}
      validationParams = keys.reduce((acc, key, index) => {
        acc[key] = t(values[index]?.toLowerCase() as string).toLowerCase();
        return acc;
      }, {} as Record<string, string>);
    }

    // Make sure the first letter of the validation string is uppercase.
    const validationTranslation = t(validation.error.toLowerCase(), validationParams);

    // If the translation is empty, return an empty string
    if (!validationTranslation) return '';

    // Capitalize the first letter
    return validationTranslation.charAt(0).toUpperCase() + validationTranslation.slice(1);
  };

  // Reset store
  const resetStore = () => {
    items.value = {} as ItemPaginationResponseObject;
    filters.value = [];
    cachedFilters.value = {};
    selectedItems.value = [];
    workingOnSelected.value = false;
    reminders.value = [];
    workingItem.value = null;
    currentAssignees.value = [];
    lastItemReminder.value = null;
    activeTenant.value = null;
    activeDocuments.value = [];
    cachedDocuments.value = {};
  };

  // Return the state, getters, and actions
  return {
    items,
    loadingItems,
    filters,
    cachedFilters,
    selectedItems,
    workingOnSelected,
    reminders,
    workingItem,
    editableItem,
    workingItemFlowState,
    currentAssignees,
    lastItemReminder,
    activeTenant,
    activeDocuments,
    cachedDocuments,
    showDocumentComments,
    workingItemDuplicates,
    cleanItemStore,
    getItemById,
    getValidationResults,
    getItems,
    getAllItems,
    clearItems,
    setLoadingItems,
    getWorkingItemFlowState,
    getFlowStatus,
    getFilteredItems,
    sendReminder,
    sendReminderToAllAssignees,
    getReminders,
    approveItem,
    declineItem,
    startFlow,
    getCachedFilters,
    setFilters,
    clearFilters,
    getItemDocuments,
    getWorkingItem,
    isAccessible,
    setWorkingItem,
    updateItem,
    getCurrentAssignees,
    getLastItemReminder,
    addComment,
    setCachedDocuments,
    getCachedDocuments,
    setActiveDocuments,
    clearWorkingItem,
    setActiveTenant,
    clearActiveTenant,
    setWorkingOnSelected,
    setShowDocumentComments,
    getItemActivities,
    getItemHistoryEvents,
    fieldValidationIssues,
    getValidationTranslation,
    deleteAttachment,
    getDuplicates,
    updateDuplicateStatus
  };
}, {
  persist: [
    {
      pick: ['activeTenant', 'workingOnSelected', 'cachedFilters'],
      storage: piniaPluginPersistedstate.cookies({sameSite: 'strict'}),
      key: 'item.active'
    }
  ]
});